<template>
  <component-wrapper>
    <div
      class="header-media-fi"
      :class="{
        'header-media-fi--slider': fields.slideElements && fields.slideElements.length > 1,
        'header-media-fi--breadcrumb': fields.showBreadcrumb?.value,
      }"
      :slider-settings="sliderSettings"
    >
      <carousel-fi
        v-if="fields.slideElements && fields.slideElements.length > 1"
        ref="carousel"
        :settings="sliderSettings"
        theme="header-media"
        @before-change="beforeSlideChanged"
        @after-change="afterSlideChanged"
      >
        <carousel-slide-fi
          v-for="(slide, index) in filteredSlideElements"
          :key="'header-media-fi__slide-' + index"
        >
          <header-media-item-fi
            v-if="!slide.fields.textRight"
            :item="slide"
            :slide-changed="slideIsChanging"
            :eager="index === 0"
            @pause-slider="pauseSlider"
          />
          <header-media-text-item-fi
            v-else
            :item="slide"
            :slide-changed="slideIsChanging"
            :eager="index === 0"
            @pause-slider="pauseSlider"
          />
        </carousel-slide-fi>
      </carousel-fi>

      <template v-else>
        <header-media-item-fi
          v-if="!fields.slideElements?.[0].fields.textRight"
          :item="fields.slideElements?.[0]"
          eager
        />

        <header-media-text-item-fi
          v-else
          :item="fields.slideElements?.[0]"
          eager
        />
      </template>
    </div>
  </component-wrapper>
</template>

<script setup lang="ts">
import { CarouselFi, CarouselSlideFi } from 'atoms';
import { computed, inject, ref, useTemplateRef, watch } from 'vue';

import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import HeaderMediaItemFi from '@/components/header-fi/header-media-fi/header-media-item-fi/HeaderMediaItemFi.vue';
import HeaderMediaTextItemFi from '@/components/header-fi/header-media-fi/header-media-text-item-fi/HeaderMediaTextItemFi.vue';
import { useVideo } from '@/composables/video';
import { useNavigationStore } from '@/store/pinia/navigation';

defineOptions({ name: 'HeaderMediaFi' });

const fields = inject<Record<string, any>>('fields', {});

const navigationStore = useNavigationStore();
const { isVideo } = useVideo();

const carousel = useTemplateRef<InstanceType<typeof CarouselFi>>('carousel');

const maxSlideElements = 5;
const slideIsChanging = ref(false);

const flyout = computed(() => navigationStore.showFlyout);

const sliderSettings = computed(() => ({
  autoplay: Boolean(fields.autoPlay?.value),
  interval: 6400,
}));

const filteredSlideElements = computed(() => {
  if (fields.slideElements) {
    return fields.slideElements.slice(0, maxSlideElements);
  }
  return {};
});

watch(flyout, (value) => {
  if (!sliderSettings.value.autoplay || !carousel.value) {
    return;
  }
  if (value) {
    (carousel.value as any).pause();
  } else {
    (carousel.value as any).play();
  }
});

const beforeSlideChanged = ({ newSlideIndex }: { newSlideIndex: number }) => {
  slideIsChanging.value = true;

  const currentSlideElement = fields.slideElements[newSlideIndex].fields;

  navigationStore.setBrandAreaIndex(newSlideIndex);

  if (isVideo(currentSlideElement)) {
    navigationStore.setShowBrandArea(true);
  }
};

const afterSlideChanged = () => {
  slideIsChanging.value = false;
};

const pauseSlider = (value: boolean) => {
  if (!sliderSettings.value.autoplay || !carousel.value) {
    return;
  }

  if (value) {
    (carousel.value as any).pause();
  } else {
    (carousel.value as any).play();
  }
};
</script>

<style src="./header-media-fi-unscoped" />
